import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Calendar,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Statistic,
  Table
} from "antd";
import { RightOutlined } from "@ant-design/icons";
import moment from "moment";
import { DATE_FORMAT_FRONTEND } from "configs/constants";
import StatusTag from "./StatusTag";
import "./Scan2PackReport.scss";

const packerDisplayName = (driver) => {
  if (!driver) return "Unassigned";
  const name = [driver.firstName, driver.lastName].filter(Boolean).join(" ");
  return name || driver.email || "Unassigned";
};

const dayKey = (date) => moment(date).format("YYYY-MM-DD");

const Scan2PackReport = ({ totes, onViewTotes }) => {
  const [selectedDate, setSelectedDate] = useState(moment());
  const [selectedWeek, setSelectedWeek] = useState(moment());

  const totesByDay = useMemo(() => {
    const map = {};
    totes.forEach((tote) => {
      if (!tote.createdAt) return;
      const key = dayKey(tote.createdAt);
      if (!map[key]) map[key] = [];
      map[key].push(tote);
    });
    return map;
  }, [totes]);

  const dayTotes = useMemo(
    () => totesByDay[dayKey(selectedDate)] || [],
    [totesByDay, selectedDate]
  );

  const statusBreakdown = useMemo(() => {
    const counts = {};
    dayTotes.forEach((tote) => {
      const status = tote.status || "";
      counts[status] = (counts[status] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);
  }, [dayTotes]);

  const dateCellRender = (date) => {
    const count = (totesByDay[dayKey(date)] || []).length;
    if (!count) return null;
    return (
      <Badge status="processing" text={`${count} ${count === 1 ? "tote" : "totes"}`} />
    );
  };

  const handleViewDay = (status) =>
    onViewTotes({
      createdDate: selectedDate.format(DATE_FORMAT_FRONTEND),
      ...(status !== undefined ? { status } : {})
    });

  const packerMetrics = useMemo(() => {
    const map = {};
    totes.forEach((tote) => {
      if (!tote.createdAt || !moment(tote.createdAt).isSame(selectedWeek, "week")) {
        return;
      }
      const key = tote.driverId || "unassigned";
      if (!map[key]) {
        map[key] = {
          key,
          packerName: packerDisplayName(tote.driver),
          totes: 0,
          paquetes: 0
        };
      }
      map[key].totes += 1;
      map[key].paquetes += tote.paquetes?.length || 0;
    });
    return Object.values(map)
      .map((row) => ({
        ...row,
        avgPaquetes: row.totes ? row.paquetes / row.totes : 0
      }))
      .sort((a, b) => b.paquetes - a.paquetes);
  }, [totes, selectedWeek]);

  const packerColumns = [
    { title: "Packer", dataIndex: "packerName", key: "packerName" },
    { title: "Totes", dataIndex: "totes", key: "totes" },
    { title: "Paquetes", dataIndex: "paquetes", key: "paquetes" },
    {
      title: "Avg Paquetes / Tote",
      dataIndex: "avgPaquetes",
      key: "avgPaquetes",
      render: (value) => value.toFixed(1)
    }
  ];

  const weekStart = selectedWeek.clone().startOf("week");
  const weekEnd = selectedWeek.clone().endOf("week");

  return (
    <div className="scan2pack-report">
      <h3 className="report-section-title">Daily Tote Report</h3>
      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Calendar
            value={selectedDate}
            onSelect={(date) => setSelectedDate(date)}
            dateCellRender={dateCellRender}
          />
        </Col>
        <Col xs={24} lg={8}>
          <Card
            className="day-summary-card"
            title={selectedDate.format("dddd, MMM D, YYYY")}
            extra={
              dayTotes.length > 0 && (
                <Button type="link" size="small" onClick={() => handleViewDay()}>
                  View totes <RightOutlined />
                </Button>
              )
            }
          >
            <Statistic title="Totes Created" value={dayTotes.length} />
            {dayTotes.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No totes created this day"
              />
            ) : (
              <div className="status-breakdown">
                <h4>By Current Status</h4>
                {statusBreakdown.map(({ status, count }) => (
                  <div
                    key={status || "unknown"}
                    className="status-breakdown-row"
                    onClick={() => handleViewDay(status)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleViewDay(status)}
                  >
                    <StatusTag status={status} />
                    <span className="status-count">{count}</span>
                    <RightOutlined className="status-arrow" />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <div className="packer-efficiency-header">
        <h3 className="report-section-title">Packer Efficiency</h3>
        <div className="week-picker">
          <span className="week-range">
            {weekStart.format("MMM D")} – {weekEnd.format("MMM D, YYYY")}
          </span>
          <DatePicker
            picker="week"
            value={selectedWeek}
            onChange={(week) => week && setSelectedWeek(week)}
            allowClear={false}
            format={() => "Select week"}
          />
        </div>
      </div>
      <Table
        dataSource={packerMetrics}
        columns={packerColumns}
        rowKey="key"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        locale={{ emptyText: "No totes created this week" }}
      />
    </div>
  );
};

export default Scan2PackReport;
